import { RepairRequest, RepairStatus } from '../types/firestore';
import { getRepairRequestUnreadState } from './repairRequestReadModel';
import {
  getRepairRequestSubjectIdentity,
  type RepairRequestSubjectIdentity,
} from '../shared/repairRequestDisplay';
import {
  getAvailableClientActions,
  getAvailableServiceActions,
  getPrimaryWorkflowAction,
  getRepairRequestProcessProgress,
  getRepairStatusMatrix,
  getRepairRequestStageLabel,
  getRepairRequestStatusMeta,
  getRepairRequestWorkflowBlocker,
  getRepairRequestWorkflowTurn,
  requiresClientAction,
  requiresServiceAction,
  type RepairRequestWorkflowAction,
  type RepairRequestWorkflowActor,
} from './repairRequestWorkflow';
import { resolveRepairWorkflowReadModel } from './repairWorkflowV15';

export type RepairCommunicationPriority = 'none' | 'info' | 'attention' | 'urgent';

export interface RepairRequestPresentationModel {
  actor: RepairRequestWorkflowActor;
  status: RepairStatus;
  statusLabel: string;
  statusVariant: ReturnType<typeof getRepairRequestStatusMeta>['badgeVariant'];
  stageLabel: string;
  stepLabel: string;
  stepIndex: number;
  stepCount: number;
  workflowTurn: ReturnType<typeof getRepairRequestWorkflowTurn>;
  workflowTurnLabel: string;
  nextStep: string;
  requiresReaction: boolean;
  reactionLabel: string;
  blocker: string | null;
  primaryAction: RepairRequestWorkflowAction | null;
  secondaryActions: RepairRequestWorkflowAction[];
  destructiveActions: RepairRequestWorkflowAction[];
  communicationPriority: RepairCommunicationPriority;
  communicationPrimarySection: 'messages' | 'quote' | 'status' | null;
  communicationSecondarySection: 'messages' | 'quote' | 'status' | null;
  communicationLabel: string | null;
  communicationSecondaryLabel: string | null;
  hasUnreadMessages: boolean;
}

const hasCanonicalIdentificationData = (request: Partial<RepairRequest>): boolean =>
  Boolean(
    request.manualDeviceType?.trim() ||
      request.equipmentTypeSelectionType ||
      request.equipmentTypeId ||
      request.equipmentTypeNameSnapshot ||
      request.equipmentTypeShortCodeSnapshot
  );

const normalizeTextValue = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeProblemDescription = (value: unknown): string =>
  String(value || '')
    .replace(/\r\n/g, '\n')
    .trim();

interface ParsedRepairRequestDescription {
  brand: string | null;
  model: string | null;
  problemDescription: string;
}

const parseRepairRequestDescription = (description: unknown): ParsedRepairRequestDescription => {
  const lines = String(description || '')
    .replace(/\r\n/g, '\n')
    .split('\n');
  let brand: string | null = null;
  let model: string | null = null;
  const freeformLines: string[] = [];
  const problemLines: string[] = [];
  let problemMode = false;

  for (const rawLine of lines) {
    const trimmedLine = rawLine.trim();

    if (!problemMode) {
      if (trimmedLine === '') {
        if (freeformLines.length > 0) {
          freeformLines.push('');
        }
        continue;
      }

      const brandMatch = trimmedLine.match(/^(marka|brand)\s*:\s*(.*)$/i);
      if (!brand && brandMatch) {
        brand = normalizeTextValue(brandMatch[2]);
        continue;
      }

      const modelMatch = trimmedLine.match(/^model\s*:\s*(.*)$/i);
      if (!model && modelMatch) {
        model = normalizeTextValue(modelMatch[1]);
        continue;
      }

      const problemMatch = trimmedLine.match(/^(opis problemu|problem|opis)\s*:\s*(.*)$/i);
      if (problemMatch) {
        problemMode = true;
        const firstProblemLine = problemMatch[2].replace(/\s+$/g, '');
        if (firstProblemLine.trim().length > 0) {
          problemLines.push(firstProblemLine);
        }
        continue;
      }

      freeformLines.push(rawLine.replace(/\s+$/g, ''));
      continue;
    }

    problemLines.push(rawLine.replace(/\s+$/g, ''));
  }

  const problemDescription = (problemMode ? [...freeformLines, ...problemLines] : freeformLines)
    .join('\n')
    .trim();

  return {
    brand,
    model,
    problemDescription,
  };
};

export interface RepairRequestDevicePresentation {
  label: string;
  source: RepairRequestSubjectIdentity['source'];
  brandModelLabel: string;
}

const buildRepairRequestIdentificationPresentation = (
  request: RepairRequest,
  maxLength = 140
): RepairRequestIdentificationPresentation => {
  const hasCanonicalIdentification = hasCanonicalIdentificationData(request);
  const parsedDescription = hasCanonicalIdentification
    ? null
    : parseRepairRequestDescription(request.description);
  const device = getRepairRequestSubjectIdentity(request);
  const brandModelLabel = hasCanonicalIdentification
    ? [normalizeTextValue(request.brand), normalizeTextValue(request.model)]
        .filter(Boolean)
        .join(' ')
    : [
        normalizeTextValue(request.brand) || parsedDescription?.brand,
        normalizeTextValue(request.model) || parsedDescription?.model,
      ]
        .filter(Boolean)
        .join(' ');
  const problemDescription = hasCanonicalIdentification
    ? normalizeProblemDescription(request.description)
    : parsedDescription?.problemDescription || '';

  return {
    device: {
      label: device.label,
      source: device.source,
      brandModelLabel,
    },
    problemDescription,
    problemSummary: problemDescription
      ? toSingleLineSummary(problemDescription, maxLength)
      : 'Brak opisu problemu od klienta.',
  };
};

export const getRepairRequestDevicePresentation = (
  request: RepairRequest
): RepairRequestDevicePresentation => buildRepairRequestIdentificationPresentation(request).device;

const toSingleLineSummary = (text: string | undefined, maxLength = 140): string => {
  const normalized = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) {
    return 'Brak opisu problemu od klienta.';
  }
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return `${normalized.slice(0, maxLength - 1)}…`;
};

export interface RepairRequestIdentificationPresentation {
  device: RepairRequestDevicePresentation;
  problemDescription: string;
  problemSummary: string;
}

export const getRepairRequestIdentificationPresentation = (
  request: RepairRequest,
  maxLength = 140
): RepairRequestIdentificationPresentation =>
  buildRepairRequestIdentificationPresentation(request, maxLength);

export const getRepairRequestIssueSummary = (request: RepairRequest, maxLength = 140): string => {
  return getRepairRequestIdentificationPresentation(request, maxLength).problemSummary;
};

const getWorkflowTurnLabel = (
  workflowTurn: ReturnType<typeof getRepairRequestWorkflowTurn>
): string => {
  if (workflowTurn === 'service') {
    return 'Następna akcja: serwis';
  }
  if (workflowTurn === 'client') {
    return 'Następna akcja: właściciel zgłoszenia';
  }
  return 'Status terminalny';
};

const getReactionLabel = (actor: RepairRequestWorkflowActor): string =>
  actor === 'service' ? 'Wymaga reakcji serwisu' : 'Wymaga Twojej reakcji';

const splitActions = (
  actor: RepairRequestWorkflowActor,
  request: RepairRequest
): {
  primaryAction: RepairRequestWorkflowAction | null;
  secondaryActions: RepairRequestWorkflowAction[];
  destructiveActions: RepairRequestWorkflowAction[];
} => {
  const workflowReadModel = resolveRepairWorkflowReadModel(request);
  const status = request.status;
  const actions =
    actor === 'service' ? getAvailableServiceActions(status) : getAvailableClientActions(status);

  if (workflowReadModel.isV15) {
    const expectedActor = workflowReadModel.nextStep.actor === 'requester' ? 'client' : 'service';
    const stepActionById: Partial<Record<string, string>> = {
      start_repair_work: 'service_start_v2_repair',
      complete_repair_work: 'service_mark_v2_repair_completed',
      prepare_cash_settlement: 'service_prepare_cash_settlement',
      confirm_cash_release: 'service_confirm_cash_settlement',
    };
    const primaryActionId = stepActionById[workflowReadModel.nextStep.id];
    const primaryAction =
      workflowReadModel.nextStep.kind === 'action' && expectedActor === actor && primaryActionId
        ? actions.find((action) => action.id === primaryActionId) || null
        : null;
    const cancellationAction =
      actor === 'service'
        ? actions.find((action) => action.id === 'service_cancel_v2_request') || null
        : status === RepairStatus.NEW
          ? actions.find((action) => action.id === 'client_cancel_new') || null
          : null;

    return {
      primaryAction,
      secondaryActions: [],
      destructiveActions: cancellationAction ? [cancellationAction] : [],
    };
  }

  const primaryAction = getPrimaryWorkflowAction(status, actor);
  const withoutPrimary = actions.filter((action) => action.id !== primaryAction?.id);
  const destructiveActions = withoutPrimary.filter((action) => action.isDestructive);
  const secondaryActions = withoutPrimary.filter((action) => !action.isDestructive);

  return {
    primaryAction,
    secondaryActions,
    destructiveActions,
  };
};

const getCommunicationLabel = ({
  actor,
  statusMatrix,
  requiresReaction,
  workflowTurn,
}: {
  actor: RepairRequestWorkflowActor;
  statusMatrix: ReturnType<typeof getRepairStatusMatrix>;
  requiresReaction: boolean;
  workflowTurn: ReturnType<typeof getRepairRequestWorkflowTurn>;
}): string | null => {
  if (statusMatrix.isTerminal) {
    return statusMatrix.listHintByActor[actor];
  }

  if (actor === 'service') {
    if (workflowTurn === 'client') {
      return 'Czekamy na właściciela zgłoszenia. W razie potrzeby przypomnij się wiadomością.';
    }
    return 'Sprawa wymaga działania i potwierdzenia po stronie właściciela zgłoszenia.';
  }

  if (workflowTurn === 'service') {
    return 'Czekamy na wycenę od serwisu. W razie potrzeby dopytaj wiadomością.';
  }

  if (requiresReaction) {
    return 'Skontaktuj się z serwisem, aby szybko domknąć ten etap.';
  }

  return null;
};

const getCommunicationSignal = (
  request: RepairRequest,
  actor: RepairRequestWorkflowActor,
  requiresReaction: boolean,
  workflowTurn: ReturnType<typeof getRepairRequestWorkflowTurn>
): Pick<
  RepairRequestPresentationModel,
  | 'communicationPriority'
  | 'communicationPrimarySection'
  | 'communicationSecondarySection'
  | 'communicationLabel'
  | 'communicationSecondaryLabel'
  | 'hasUnreadMessages'
> => {
  const unread = getRepairRequestUnreadState(request, actor);
  const primarySection = unread.primarySection;
  const secondarySection = unread.secondarySection;
  const hasUnreadMessages = unread.hasUnreadMessages;
  const hasFreshRequestActivity = unread.hasFreshRequestActivity;
  const statusMatrix = getRepairStatusMatrix(request.status);
  const communicationLabel = getCommunicationLabel({
    actor,
    statusMatrix,
    requiresReaction,
    workflowTurn,
  });
  const communicationSecondaryLabel = null;

  if (actor === 'service') {
    if (primarySection === 'messages' && !hasFreshRequestActivity) {
      return {
        communicationPriority: 'urgent',
        communicationPrimarySection: primarySection,
        communicationSecondarySection: secondarySection,
        communicationLabel,
        communicationSecondaryLabel,
        hasUnreadMessages,
      };
    }

    if (primarySection === 'quote') {
      return {
        communicationPriority: 'attention',
        communicationPrimarySection: primarySection,
        communicationSecondarySection: secondarySection,
        communicationLabel,
        communicationSecondaryLabel,
        hasUnreadMessages,
      };
    }

    if (primarySection === 'status') {
      return {
        communicationPriority: 'info',
        communicationPrimarySection: primarySection,
        communicationSecondarySection: secondarySection,
        communicationLabel,
        communicationSecondaryLabel,
        hasUnreadMessages,
      };
    }

    if (requiresReaction) {
      return {
        communicationPriority: 'attention',
        communicationPrimarySection: null,
        communicationSecondarySection: null,
        communicationLabel,
        communicationSecondaryLabel,
        hasUnreadMessages,
      };
    }

    if (workflowTurn === 'client') {
      return {
        communicationPriority: 'info',
        communicationPrimarySection: null,
        communicationSecondarySection: null,
        communicationLabel,
        communicationSecondaryLabel,
        hasUnreadMessages,
      };
    }

    return {
      communicationPriority: 'none',
      communicationPrimarySection: null,
      communicationSecondarySection: null,
      communicationLabel,
      communicationSecondaryLabel,
      hasUnreadMessages,
    };
  }

  if (!primarySection) {
    return {
      communicationPriority: requiresReaction ? 'attention' : 'none',
      communicationPrimarySection: null,
      communicationSecondarySection: null,
      communicationLabel,
      communicationSecondaryLabel,
      hasUnreadMessages,
    };
  }

  if (primarySection === 'messages') {
    return {
      communicationPriority: 'urgent',
      communicationPrimarySection: primarySection,
      communicationSecondarySection: secondarySection,
      communicationLabel,
      communicationSecondaryLabel,
      hasUnreadMessages,
    };
  }

  return {
    communicationPriority: 'attention',
    communicationPrimarySection: primarySection,
    communicationSecondarySection: secondarySection,
    communicationLabel,
    communicationSecondaryLabel,
    hasUnreadMessages,
  };
};

export const getRepairRequestPresentation = (
  request: RepairRequest,
  actor: RepairRequestWorkflowActor
): RepairRequestPresentationModel => {
  const statusMeta = getRepairRequestStatusMeta(request.status);
  const statusMatrix = getRepairStatusMatrix(request.status);
  const stageLabel = getRepairRequestStageLabel(request.status);
  const workflowTurn = getRepairRequestWorkflowTurn(request.status);
  const blocker = getRepairRequestWorkflowBlocker(request.status, actor);
  const requiresReaction =
    actor === 'service'
      ? (request.serviceHasPendingAction ?? requiresServiceAction(request.status))
      : (request.clientHasPendingAction ?? requiresClientAction(request.status));
  const { primaryAction, secondaryActions, destructiveActions } = splitActions(actor, request);
  const processProgress = getRepairRequestProcessProgress(request.status);
  const baseNextStep = statusMatrix.nextStepByActor[actor];
  const nextStep =
    statusMatrix.nextActionOwner === 'system'
      ? `Status terminalny: ${baseNextStep}`
      : statusMatrix.nextActionOwner === actor
        ? `Twoja akcja: ${baseNextStep}`
        : `Akcja po stronie ${statusMatrix.nextActionOwner === 'service' ? 'serwisu' : 'właściciela zgłoszenia'}: ${baseNextStep}`;

  const communicationSignal = getCommunicationSignal(
    request,
    actor,
    requiresReaction,
    workflowTurn
  );

  return {
    actor,
    status: request.status,
    statusLabel: statusMeta.label,
    statusVariant: statusMeta.badgeVariant,
    stageLabel,
    stepLabel: `Krok ${processProgress.currentStepIndex + 1}/${processProgress.totalStepCount}`,
    stepIndex: processProgress.currentStepIndex,
    stepCount: processProgress.totalStepCount,
    workflowTurn,
    workflowTurnLabel: getWorkflowTurnLabel(workflowTurn),
    nextStep,
    requiresReaction,
    reactionLabel: getReactionLabel(actor),
    blocker,
    primaryAction,
    secondaryActions,
    destructiveActions,
    ...communicationSignal,
  };
};
